"use client";

import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { ZoblissLogo } from "../icons/common";
import {
  ArrowRightIcon,
  AwardIcon,
  BanknoteIcon,
  BarChart3Icon,
  BellIcon,
  BrushIcon,
  ChartPieIcon,
  DatabaseIcon,
  MailIcon,
  MessageSquareTextIcon,
  Calendar,
  ClipboardListIcon,
  CreditCardIcon,
  DoorOpenIcon,
  EyeIcon,
  GiftIcon,
  HandMetalIcon,
  LandmarkIcon,
  MapPinIcon,
  MegaphoneIcon,
  MonitorIcon,
  NotebookPenIcon,
  PackageIcon,
  PaletteIcon,
  PenIcon,
  PenToolIcon,
  ReceiptIcon,
  ScissorsIcon,
  ShoppingBagIcon,
  StethoscopeIcon,
  SunIcon,
  UsersRound,
  WavesIcon,
  ZapIcon,
} from "lucide-react";
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenu,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  NavigationMenuIndicator,
} from "@/components/ui/navigation-menu";
import * as React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useSession } from "next-auth/react";
import { trackButtonClick, trackLead } from "@/lib/gtag";
const navLinks = [
  {
    href: "/solutions",
    label: "Solutions",
    // Hover a category on the left to swap the badges, description and photo
    solutionsMenu: true,
    menu: [
      {
        category: "Beauty",
        description:
          "Booking, payments and client history built for salons, barbershops and studios, so every chair stays full.",
        image: "/images/landing_page/types/1.jpg",
        items: [
          {
            title: "Hair Salons",
            href: "/solutions/hair-salons",
            icon: ScissorsIcon,
          },
          {
            title: "Barbershops",
            href: "/solutions/barbershops",
            icon: BrushIcon,
          },
          {
            title: "Nail Studios",
            href: "/solutions/nail-studios",
            icon: PenToolIcon,
          },
          {
            title: "Brow & Lash",
            href: "/solutions/brow-lash",
            icon: EyeIcon,
          },
          {
            title: "Makeup Artists",
            href: "/solutions/makeup-artists",
            icon: PaletteIcon,
          },
        ],
      },
      {
        category: "Wellness",
        description:
          "Intake forms, SOAP notes, rooms and memberships for spas, massage therapists and medspas.",
        image: "/images/landing_page/types/6.jpg",
        items: [
          {
            title: "Day Spas",
            href: "/solutions/spas",
            icon: WavesIcon,
          },
          {
            title: "Massage Therapy",
            href: "/solutions/massage",
            icon: HandMetalIcon,
          },
          {
            title: "Medspas",
            href: "/solutions/med-spas",
            icon: StethoscopeIcon,
          },
        ],
      },
      {
        category: "Specialized Services",
        description:
          "Consent forms, deposits and repeat-visit scheduling for tattoo, tanning and hair removal studios.",
        image: "/images/landing_page/types/7.jpg",
        items: [
          {
            title: "Tattoo & Piercing",
            href: "/solutions/tattoo-piercing",
            icon: PenIcon,
          },
          {
            title: "Tanning Centers",
            href: "/solutions/tanning",
            icon: SunIcon,
          },
          {
            title: "Hair Removal",
            href: "/solutions/hair-removal",
            icon: ZapIcon,
          },
        ],
      },
    ],
  },
  {
    href: "/features",
    label: "Features",
    // Mega menu: categories stacked into three columns, promo cards on the right
    megaMenu: true,
    columns: [
      ["Run Your Business"],
      ["Grow Your Business", "Simplify Payments"],
      ["Elevate Client Experience", "Build Your Brand"],
    ],
    footerLink: { label: "View All features", href: "/features" },
    promos: [
      {
        title: "Online booking, 24/7",
        description: "Clients book, pay a deposit and rebook from any device",
        href: "/features/online-booking",
        image: "/images/landing_page/how-1.png",
      },
      {
        title: "Free data transfer",
        description:
          "We move your clients, services and history from your old software",
        href: "/features/data-transfer",
      },
    ],
    menu: [
      {
        category: "Run Your Business",
        items: [
          {
            title: "Calendar",
            href: "/features/booking-scheduling",
            icon: Calendar,
          },
          {
            title: "POS & Kiosk",
            href: "/features/pos-kiosk",
            icon: MonitorIcon,
          },
          {
            title: "Team Members",
            href: "/features/staff-management",
            icon: LandmarkIcon,
          },
          {
            title: "Reports",
            href: "/features/reports",
            icon: ChartPieIcon,
          },
          {
            title: "Intake Forms",
            href: "/features/intake-forms",
            icon: ClipboardListIcon,
          },
          {
            title: "SOAP Notes",
            href: "/features/soap-notes",
            icon: NotebookPenIcon,
          },
          {
            title: "Payroll",
            href: "/features/payroll",
            icon: BanknoteIcon,
          },
        ],
      },
      {
        category: "Grow Your Business",
        items: [
          {
            title: "Memberships",
            href: "/features/memberships",
            icon: AwardIcon,
          },
          {
            title: "Gift Cards",
            href: "/features/gift-cards",
            icon: GiftIcon,
          },
          {
            title: "Inventory",
            href: "/features/inventory-retail-management",
            icon: PackageIcon,
          },
          {
            title: "Multi-Location",
            href: "/features/multi-location",
            icon: MapPinIcon,
          },
          {
            title: "Rooms & Resources",
            href: "/features/resource-management",
            icon: DoorOpenIcon,
          },
        ],
      },
      {
        category: "Simplify Payments",
        items: [
          {
            title: "Invoices",
            href: "/features/invoices",
            icon: ReceiptIcon,
          },
          {
            title: "Payments",
            href: "/features/payments",
            icon: CreditCardIcon,
          },
          {
            title: "Sales",
            href: "/features/sales",
            icon: BarChart3Icon,
          },
        ],
      },
      // {
      //   category: "Client Experience",
      //   items: [
      //     {
      //       title: "Online Booking",
      //       href: "/features/booking-scheduling",
      //       icon: MousePointer2Icon,
      //     },
      //     {
      //       title: "Customer Tracking",
      //       href: "/features/client-management",
      //       icon: UserRoundIcon,
      //     },
      //     {
      //       title: "Notifications",
      //       href: "/features/marketing",
      //       icon: BellIcon,
      //     },
      //   ],
      // },
      {
        category: "Elevate Client Experience",
        items: [
          {
            title: "Online Booking",
            href: "/features/online-booking",
            icon: ShoppingBagIcon,
          },
          {
            title: "Clients",
            href: "/features/client-management",
            icon: UsersRound,
          },
          {
            title: "Notifications",
            href: "/features/notifications",
            icon: BellIcon,
          },
        ],
      },
      {
        category: "Build Your Brand",
        items: [
          {
            title: "Marketing",
            tagline: "AI-powered campaigns to fill your book.",
            href: "/features/marketing",
            icon: MegaphoneIcon,
          },
          {
            title: "Email Marketing",
            href: "/features/email-marketing",
            icon: MailIcon,
          },
          {
            title: "Text Marketing",
            href: "/features/text-marketing",
            icon: MessageSquareTextIcon,
          },
          {
            title: "Design Services",
            tagline: "Custom branding for your business.",
            href: "/features/branding",
            icon: PaletteIcon,
          },
        ],
      },
    ],
  },
  { href: "/pricing", label: "Pricing" },
  { href: "/get-demo", label: "Get Demo" },
];

function FeaturesMegaMenu({ link, isActiveHref, onNavigate }) {
  const byCategory = Object.fromEntries(
    link.menu.map((group) => [group.category, group]),
  );

  return (
    <div className="mx-auto flex max-w-7xl gap-10 px-10 py-10">
      <div className="flex flex-1 flex-col">
        <div className="grid flex-1 grid-cols-3 gap-8">
          {link.columns.map((categories) => (
            <div key={categories.join()} className="space-y-14">
              {categories.map((category) => {
                const group = byCategory[category];
                if (!group) return null;
                return (
                  <div key={category}>
                    <p className="mb-5 text-[13px] font-medium tracking-wider text-neutral-600 uppercase">
                      {group.category}
                    </p>
                    <ul className="space-y-3.5">
                      {group.items.map((item) => (
                        <li key={item.title}>
                          <NavigationMenuLink asChild>
                            <Link
                              href={item.href}
                              onClick={() => onNavigate(item.title)}
                              className={cn(
                                "inline-block cursor-pointer text-base text-neutral-900 underline-offset-4 transition-colors hover:text-neutral-950 hover:underline focus:underline",
                                isActiveHref(item.href) &&
                                  "underline decoration-neutral-900 decoration-2",
                              )}
                            >
                              {item.title}
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {link.promos?.length > 0 && (
        <div className="flex w-[300px] shrink-0 flex-col gap-4 border-l border-neutral-200 pl-10">
          {link.promos.map((promo) => (
            <NavigationMenuLink asChild key={promo.title}>
              <Link
                href={promo.href}
                onClick={() => onNavigate(promo.title)}
                className="group block cursor-pointer overflow-hidden rounded-md bg-[#efecea] transition-colors hover:bg-[#e0dfdf]"
              >
                <div className="p-4">
                  <p className="text-base font-medium text-neutral-900">{promo.title}</p>
                  <p className="mt-1 text-sm leading-snug text-neutral-600">
                    {promo.description}
                  </p>
                </div>
                {promo.image && (
                  <div className="relative mx-4 mb-4 h-40 overflow-hidden rounded-md">
                    <Image
                      src={promo.image}
                      alt=""
                      fill
                      sizes="268px"
                      className="object-cover object-[50%_25%] transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                  </div>
                )}
              </Link>
            </NavigationMenuLink>
          ))}
          {link.footerLink && (
            <NavigationMenuLink asChild>
              <Link
                href={link.footerLink.href}
                onClick={() => onNavigate(link.footerLink.label)}
                className="group flex cursor-pointer items-center justify-between rounded-md bg-[#efecea] p-4 text-base font-medium text-neutral-900 transition-colors hover:bg-[#e0dfdf]"
              >
                {link.footerLink.label}
                <ArrowRightIcon
                  className="size-4 transition-transform group-hover:translate-x-0.5"
                  strokeWidth={1.75}
                />
              </Link>
            </NavigationMenuLink>
          )}
        </div>
      )}
    </div>
  );
}

function SolutionsMegaMenu({ link, onNavigate }) {
  const [activeCategory, setActiveCategory] = useState(link.menu[0].category);
  const active =
    link.menu.find((group) => group.category === activeCategory) ??
    link.menu[0];

  return (
    <div className="mx-auto flex max-w-7xl gap-10 px-10 py-10">
      {/* Categories */}
      <div className="w-56 shrink-0 border-r border-neutral-200 pr-10">
        <p className="mb-5 text-[13px] font-medium tracking-wider text-neutral-600 uppercase">
          Solutions for
        </p>
        <ul className="space-y-3.5">
          {link.menu.map((group) => (
            <li key={group.category}>
              <button
                type="button"
                onMouseEnter={() => setActiveCategory(group.category)}
                onFocus={() => setActiveCategory(group.category)}
                onClick={() => setActiveCategory(group.category)}
                className={cn(
                  "cursor-pointer text-left text-[17px] transition-colors font-medium underline-offset-4",
                  group.category === active.category
                    ? "text-neutral-900 underline"
                    : "text-neutral-700 hover:text-neutral-900",
                )}
              >
                {group.category}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Business types in the active category */}
      <div className="flex flex-1 flex-col">
        <p className="mb-5 text-[13px] font-medium tracking-wider text-neutral-600 uppercase">
          Explore
        </p>
        <p className="text-[17px] font-medium text-neutral-900">{active.category}</p>
        <ul className="mt-5 flex max-w-md flex-wrap gap-2.5">
          {active.items.map((item) => (
            <li key={item.title}>
              <NavigationMenuLink asChild>
                <Link
                  href={item.href}
                  onClick={() => onNavigate(item.title)}
                  className="inline-flex cursor-pointer items-center rounded-full bg-neutral-100 px-4 py-2 text-[15px] text-neutral-900 transition-colors hover:bg-neutral-900 hover:text-white"
                >
                  {item.title}
                </Link>
              </NavigationMenuLink>
            </li>
          ))}
        </ul>
        <p className="mt-auto max-w-md pt-10 text-base leading-relaxed text-neutral-600">
          {active.description}
        </p>
      </div>

      {/* Photo; all stacked so switching is instant */}
      <div className="relative h-[340px] w-[270px] shrink-0 overflow-hidden rounded-md bg-neutral-100">
        {link.menu.map((group) => (
          <Image
            key={group.category}
            src={group.image}
            alt={group.category}
            fill
            sizes="270px"
            className={cn(
              "object-cover transition-opacity duration-300",
              group.category === active.category ? "opacity-100" : "opacity-0",
            )}
          />
        ))}
      </div>
    </div>
  );
}

export function Header({ clasName = "" }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const dashboardHref =
    session?.role === "super_admin" ? "/admin/home" : "/calendar";

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 0);
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Check initial scroll position
    handleScroll();

    // Cleanup
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when clicking on nav links
  const handleNavLinkClick = (label) => {
    setIsMobileMenuOpen(false);
    if (label) {
      trackButtonClick(`nav_${label.toLowerCase().replace(/\s+/g, "_")}`, {
        location: isMobileMenuOpen ? "mobile_header" : "desktop_header",
      });
    }
  };

  const isActiveHref = (href) =>
    href === "/"
      ? pathname === href
      : pathname === href || pathname.startsWith(`${href}/`);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  return (
    <header
      className={cn(
        "sticky inset-x-0 top-0 z-30 w-full transition-all",
        isScrolled ? "bg-white" : "bg-[#efecea] pt-4 md:pt-7",
      )}
    >
      <div
        className={cn(
          "absolute inset-0 top-7 block rounded-[26px] transition-all duration-200",
          isScrolled
            ? "border-b border-neutral-100 bg-white/75 backdrop-blur-lg"
            : "border-b border-transparent bg-transparent",
        )}
      ></div>
      <div
        className={cn(
          "relative mx-1.5 max-w-7xl rounded-[26px] bg-white px-3 md:mx-auto md:w-full lg:px-4 lg:py-2 xl:px-4",
          clasName,
        )}
      >
        <div className="flex h-14 items-center justify-between md:h-14">
          <Link href="/" className="basis-0">
            <ZoblissLogo className="h-auto w-38" />
          </Link>

          <div className="flex items-center gap-8">
            {/* Root is static so the panel lines up with the header bar, not the triggers */}
            <NavigationMenu className="text-md hidden items-center md:static md:flex">
              <NavigationMenuViewport
                wrapperClassName="inset-x-0 left-0"
                className="top-2 rounded-3xl border-none bg-transparent shadow-[0_8px_50px_#80808026] md:w-full"
              />
              {/*<NavigationMenuViewport className="md:w-full right-0 top-23.5 fixed rounded-none rounded-b-4xl border-t-0  h-[460px] shadow-[0px_12px_14px_#00000017]" />{" "}*/}
              {/*bg-black/20 backdrop-blur-xs*/}
              <NavigationMenuList>
                {navLinks.map((link) => (
                  <NavigationMenuItem key={link.label} value={link.label}>
                    {link.menu ? (
                      <>
                        <NavigationMenuTrigger className="flex items-center rounded-lg px-4 py-2 text-base font-medium text-neutral-800 transition-colors hover:bg-neutral-900/5 hover:text-neutral-900">
                          {link.label}
                          {isActiveHref(link.href) && (
                            <div className="bg-fe-primary absolute top-full left-4 right-9 -mt-1 hidden h-[3px] rounded-full lg:block"></div>
                          )}
                        </NavigationMenuTrigger>
                        {link.solutionsMenu ? (
                          <NavigationMenuContent className="bg-white p-0 md:w-full">
                            <SolutionsMegaMenu
                              link={link}
                              onNavigate={handleNavLinkClick}
                            />
                          </NavigationMenuContent>
                        ) : link.megaMenu ? (
                          <NavigationMenuContent className="bg-white p-0 md:w-full">
                            <FeaturesMegaMenu
                              link={link}
                              isActiveHref={isActiveHref}
                              onNavigate={handleNavLinkClick}
                            />
                          </NavigationMenuContent>
                        ) : (
                        <NavigationMenuContent className="bg-white md:w-full">
                          {/*<NavigationMenuContent className="md:w-full">*/}
                          <ul
                            className={cn(
                              "mx-auto flex max-w-7xl gap-20 p-8 pt-6",
                              link.menu.length > 4 && "justify-between gap-6",
                            )}
                          >
                            {link.menu.map((menuItems) => {
                              return (
                                <div key={menuItems.category}>
                                  <h1 className="relative mb-6 inline-block text-lg font-medium text-neutral-800">
                                    {menuItems.category}
                                    <span className="vai-[#ff5e62] absolute -bottom-1.5 left-0 h-1 w-full rounded-full bg-linear-to-r from-[#ff9966] to-[#ffc53d] opacity-75"></span>
                                  </h1>
                                  {menuItems.items.map((menuItem) => (
                                    <li key={menuItem.title}>
                                      <NavigationMenuLink asChild>
                                        <Link
                                          href={menuItem.href}
                                          className={cn(
                                            "group block space-y-1 rounded-lg py-2 leading-none underline-offset-8 select-none hover:underline focus:underline",
                                            isActiveHref(menuItem.href) &&
                                              "decoration-fe-primary underline decoration-2",
                                          )}
                                        >
                                          <div className="flex items-center space-x-4 text-neutral-800">
                                            <div className="flex shrink-0">
                                              <menuItem.icon
                                                className="size-[22px] text-[#11110D]"
                                                strokeWidth={1.5}
                                              />
                                            </div>
                                            <h6 className="text-lg text-neutral-800">
                                              {menuItem.title}
                                            </h6>
                                          </div>
                                        </Link>
                                      </NavigationMenuLink>
                                    </li>
                                  ))}
                                </div>
                              );
                            })}
                          </ul>
                        </NavigationMenuContent>
                        )}
                      </>
                    ) : (
                      <NavigationMenuLink asChild>
                        <Link
                          className="flex items-center rounded-lg px-4 py-2 text-base font-medium text-neutral-800 transition-colors hover:bg-neutral-900/5 hover:text-neutral-900"
                          href={link.href}
                          onClick={() => handleNavLinkClick(link.label)}
                        >
                          {link.label}
                          {isActiveHref(link.href) && (
                            <div className="bg-fe-primary absolute top-full inset-x-4 -mt-1 hidden h-[3px] rounded-full lg:block"></div>
                          )}
                        </Link>
                      </NavigationMenuLink>
                    )}
                  </NavigationMenuItem>
                ))}
                {/*<NavigationMenuIndicator  />*/}
              </NavigationMenuList>
              <NavigationMenuIndicator />
            </NavigationMenu>

            {/* Mobile menu */}
            <nav
              className={cn(
                "fixed inset-0 z-20 max-h-screen w-full overflow-y-auto bg-white px-5 py-16 lg:hidden dark:bg-black dark:text-white/70",
                isMobileMenuOpen ? "block" : "hidden",
              )}
            >
              <ul className="grid divide-y divide-neutral-200 dark:divide-white/[0.15]">
                {navLinks.map((link) => (
                  <li key={link.label} className="py-3">
                    {link.menu ? (
                      <Accordion
                        type="single"
                        collapsible
                        className="w-full md:w-md"
                      >
                        <AccordionItem value="1">
                          <AccordionTrigger
                            className={cn(
                              "rounded-none py-0",
                              isActiveHref(link.href) &&
                                "decoration-fe-primary underline decoration-2 underline-offset-8",
                            )}
                            arrowClassName="size-4 text-neutral-900"
                          >
                            {link.label}
                          </AccordionTrigger>
                          <AccordionContent>
                            <div
                              className={cn(
                                "mt-4 flex flex-col gap-6 rounded-2xl bg-neutral-100 px-3 py-6",
                              )}
                            >
                              {link.menu.map((menuItems) => {
                                return (
                                  <div key={menuItems.category}>
                                    <h1 className="relative mb-6 inline-block text-base text-neutral-800">
                                      {menuItems.category}
                                      <span className="vai-[#ff5e62] absolute -bottom-1 left-0 h-0.5 w-full rounded-full bg-gradient-to-r from-[#ff9966] to-[#ffc53d]"></span>
                                    </h1>
                                    <div className="grid grid-cols-1">
                                      {menuItems.items.map((menuItem) => (
                                        <div key={menuItem.title}>
                                          <Link
                                            href={menuItem.href}
                                            className={cn(
                                              "group block space-y-1 rounded-lg py-3 leading-none underline-offset-8 select-none hover:underline focus:underline",
                                              isActiveHref(menuItem.href) &&
                                                "decoration-fe-primary underline decoration-2",
                                            )}
                                          >
                                            <div className="flex items-center space-x-4 text-neutral-800">
                                              <div className="flex shrink-0">
                                                <menuItem.icon
                                                  className="size-[22px] text-[#11110D]"
                                                  strokeWidth={1.5}
                                                />
                                              </div>
                                              <h6 className="text-base text-neutral-800">
                                                {menuItem.title}
                                              </h6>
                                            </div>
                                          </Link>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ) : (
                      <Link
                        className={cn(
                          "flex w-full font-medium capitalize",
                          isActiveHref(link.href) &&
                            "decoration-fe-primary underline decoration-2 underline-offset-8",
                        )}
                        href={link.href}
                        onClick={() => handleNavLinkClick(link.label)}
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
              <div className="py-3">
                <Button
                  className={cn(
                    "bg-fe-primary h-9 w-full px-4 hover:bg-neutral-800 hover:ring-4 hover:ring-neutral-200",
                  )}
                  asChild
                >
                  <Link
                    href="/signup"
                    onClick={() =>
                      trackLead({
                        source: "header_mobile",
                        cta_label: "try_it_for_free",
                      })
                    }
                  >
                    Try it for free
                  </Link>
                </Button>
              </div>
            </nav>
          </div>

          <div className="flex items-center justify-end gap-3">
            {session ? (
              <Link
                href={dashboardHref}
                className="text-content-emphasis hover:bg-muted flex h-9 items-center rounded-lg border border-[#11110d52] bg-white px-4 text-sm font-medium md:h-11"
                onClick={() =>
                  trackButtonClick("dashboard", {
                    location: "header",
                  })
                }
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  href="/login"
                  className="text-content-emphasis hover:bg-muted flex h-9 items-center rounded-lg border border-[#11110d52] bg-white px-4 text-sm font-medium md:h-11"
                  onClick={() =>
                    trackButtonClick("login", {
                      location: "header",
                    })
                  }
                >
                  Log in
                </Link>
                <Button
                  className={cn(
                    "bg-fe-primary hover:ring-fe-primary/50 hover:fe-primary hidden h-9 px-4 font-medium text-white hover:ring-4 md:inline-flex md:h-11",
                  )}
                  asChild
                >
                  <Link
                    href="/signup"
                    onClick={() =>
                      trackLead({
                        source: "header_desktop",
                        cta_label: "try_it_for_free",
                      })
                    }
                  >
                    Try it for free
                  </Link>
                </Button>
              </>
            )}

            <button
              className="z-30 rounded-full p-2 transition-colors duration-200 hover:bg-neutral-200 focus:outline-none active:bg-neutral-300 md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                // Close icon (X)
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5 text-neutral-600 dark:text-white/70"
                >
                  <path d="m6 6 12 12"></path>
                  <path d="m18 6-12 12"></path>
                </svg>
              ) : (
                // Menu icon (hamburger)
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5 text-neutral-600 dark:text-white/70"
                >
                  <line x1="4" x2="20" y1="12" y2="12"></line>
                  <line x1="4" x2="20" y1="6" y2="6"></line>
                  <line x1="4" x2="20" y1="18" y2="18"></line>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
